import { useMemo } from "react";

const hexagons: Array<{
	size: number;
	x: string;
	y: string;
	opacity: number;
	outline: boolean;
	blur?: number;
	animation: "hex-float" | "hex-drift";
	duration: string;
	delay: string;
	color?: string;
}> = [
	// Row 1 — top band
	{ size: 36, x: "6%", y: "5%", opacity: 0.06, outline: false, animation: "hex-drift", duration: "42s", delay: "0s", blur: 1 },
	{ size: 14, x: "46%", y: "8%", opacity: 0.12, outline: true, animation: "hex-float", duration: "26s", delay: "-4s" },
	{ size: 44, x: "88%", y: "4%", opacity: 0.04, outline: false, animation: "hex-float", duration: "50s", delay: "-18s", blur: 2 },
	// Row 2 — upper-mid
	{ size: 18, x: "24%", y: "24%", opacity: 0.1, outline: true, animation: "hex-drift", duration: "32s", delay: "-10s" },
	{ size: 28, x: "66%", y: "20%", opacity: 0.05, outline: false, animation: "hex-float", duration: "44s", delay: "-7s", color: "#d946ef" },
	{ size: 12, x: "94%", y: "28%", opacity: 0.13, outline: true, animation: "hex-float", duration: "24s", delay: "-14s" },
	// Row 3 — mid
	{ size: 52, x: "3%", y: "42%", opacity: 0.03, outline: false, animation: "hex-float", duration: "56s", delay: "-22s", blur: 3 },
	{ size: 20, x: "42%", y: "38%", opacity: 0.08, outline: true, animation: "hex-drift", duration: "30s", delay: "-5s" },
	{ size: 40, x: "76%", y: "44%", opacity: 0.05, outline: false, animation: "hex-drift", duration: "48s", delay: "-12s" },
	// Row 4 — lower-mid
	{ size: 16, x: "18%", y: "60%", opacity: 0.1, outline: true, animation: "hex-float", duration: "28s", delay: "-16s" },
	{ size: 48, x: "56%", y: "58%", opacity: 0.04, outline: false, animation: "hex-drift", duration: "46s", delay: "-8s", blur: 2 },
	{ size: 22, x: "90%", y: "62%", opacity: 0.09, outline: true, animation: "hex-float", duration: "34s", delay: "-20s" },
	// Row 5 — bottom band
	{ size: 32, x: "8%", y: "80%", opacity: 0.05, outline: false, animation: "hex-drift", duration: "40s", delay: "-3s", blur: 1 },
	{ size: 18, x: "38%", y: "82%", opacity: 0.09, outline: true, animation: "hex-float", duration: "30s", delay: "-11s" },
	{ size: 26, x: "68%", y: "78%", opacity: 0.06, outline: false, animation: "hex-float", duration: "38s", delay: "-15s" },
	{ size: 14, x: "92%", y: "86%", opacity: 0.11, outline: true, animation: "hex-drift", duration: "26s", delay: "-2s" },
];

export function FloatingHexagons() {
	const rendered = useMemo(
		() =>
			hexagons.map((h, i) => {
				const style: React.CSSProperties = {
					width: h.size,
					height: h.size,
					left: h.x,
					top: h.y,
					color: h.color ?? "#b876f9",
					background: h.outline ? undefined : (h.color ?? "#b876f9"),
					opacity: h.outline ? h.opacity * 1.4 : h.opacity,
					filter: h.blur ? `blur(${h.blur}px)` : undefined,
					animation: `${h.animation} ${h.duration} ease-in-out infinite, hex-pulse ${h.duration} ease-in-out infinite`,
					animationDelay: `${h.delay}, ${h.delay}`,
					["--hex-opacity" as string]: h.outline
						? h.opacity * 1.4
						: h.opacity,
				};
				return (
					<div
						key={i}
						className={h.outline ? "hex-outline" : "hex"}
						style={style}
					/>
				);
			}),
		[],
	);

	return (
		<div
			className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
			aria-hidden="true"
		>
			{rendered}
		</div>
	);
}
